// ===================================================================
// 📋 CONTEXTO DE PROCESSO SELECIONADO - ADMIN (persiste entre abas)
// ===================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/apiClient';
import { INGRESSA_STORAGE_KEYS } from '../utils/ingressaBrand';

const STORAGE_KEY = INGRESSA_STORAGE_KEYS.adminSelectedProcess;

const AdminProcessContext = createContext({
  selectedProcessId: '',
  setSelectedProcessId: () => {},
  processes: [],
  isLoadingProcesses: true,
  refreshProcesses: async () => [],
});

export const AdminProcessProvider = React.memo(({ children, userRole }) => {
  const [processes, setProcesses] = useState([]);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(true);
  const [selectedProcessId, setSelectedProcessIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  });

  const refreshProcesses = useCallback(async () => {
    try {
      const forAnalyst = userRole === 'analyst';
      const data = await apiClient.getProcesses(true, forAnalyst);
      const list = Array.isArray(data) ? data : [];
      setProcesses(list);
      return list;
    } catch (e) {
      setProcesses([]);
      return [];
    }
  }, [userRole]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingProcesses(true);
      const list = await refreshProcesses();
      if (cancelled) return;
      const stored = localStorage.getItem(STORAGE_KEY) || '';
      const storedInList = stored && list.some((p) => String(p.id) === stored);
      if (list.length && (!stored || !storedInList)) {
        const first = String(list[0].id);
        setSelectedProcessIdState(first);
        try {
          localStorage.setItem(STORAGE_KEY, first);
        } catch (e) {}
      }
      setIsLoadingProcesses(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshProcesses]);

  const setSelectedProcessId = useCallback((idOrEvent) => {
    const id = typeof idOrEvent === 'object' && idOrEvent?.target
      ? idOrEvent.target.value
      : String(idOrEvent ?? '');
    setSelectedProcessIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch (e) {}
  }, []);

  const value = {
    selectedProcessId,
    setSelectedProcessId,
    processes,
    isLoadingProcesses,
    refreshProcesses,
  };

  return (
    <AdminProcessContext.Provider value={value}>
      {children}
    </AdminProcessContext.Provider>
  );
});

export const useAdminProcess = () => useContext(AdminProcessContext);
